import { useEffect, useRef, useState, useCallback } from 'react';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const TRADE_RING = 60;

/** Apply a fresh best bid/ask to a depth snapshot: drop crossed levels, insert/update the inside level. */
function patchSide(levels, best, isBid) {
  if (!Array.isArray(levels) || !best || !(best[0] > 0)) return levels;
  const [px, sz] = best;
  if (levels.length && levels[0][0] === px && levels[0][1] === sz) return levels;
  const out = levels.filter((l) => (isBid ? l[0] <= px : l[0] >= px));
  if (out.length && out[0][0] === px) out[0] = [px, sz, out[0][2]];
  else out.unshift([px, sz, 1]);
  return out;
}

function mergeBboIntoBook(book, bbo) {
  const bids = patchSide(book.bids, bbo.bid, true);
  const asks = patchSide(book.asks, bbo.ask, false);
  if (bids === book.bids && asks === book.asks) return book;
  return { ...book, bids, asks, t: bbo.t || book.t };
}

/**
 * Live Hyperliquid market data over SSE (server fans out one shared HL WebSocket).
 * Returns { book, bbo, ctx, trades, status, latencyMs, reconnect }.
 *  - book: { t, bids:[[px,sz,n]], asks:[[px,sz,n]] } full depth, refreshed every ~1-3s by HL
 *  - bbo:  { t, bid:[px,sz], ask:[px,sz] } top of book, ~5 updates/s
 */
export default function useMarketStream(coin, dex = 'main', { apiPrefix = 'disc-scalping', enabled = true } = {}) {
  const [book, setBook] = useState(null);
  const [bbo, setBbo] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [trades, setTrades] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [latencyMs, setLatencyMs] = useState(null);
  const [gen, setGen] = useState(0);
  const esRef = useRef(null);

  const reconnect = useCallback(() => setGen((g) => g + 1), []);

  useEffect(() => {
    if (!coin || !enabled) {
      setStatus('idle');
      return undefined;
    }
    const token = localStorage.getItem('sessionToken') || '';
    const url = `${API_BASE}/${apiPrefix}/stream?coin=${encodeURIComponent(coin)}&dex=${encodeURIComponent(
      dex || 'main'
    )}&token=${encodeURIComponent(token)}`;

    setBook(null);
    setBbo(null);
    setCtx(null);
    setTrades([]);
    setError(null);
    setStatus('connecting');

    const es = new EventSource(url);
    esRef.current = es;
    let closed = false;
    const parse = (ev) => {
      try {
        return JSON.parse(ev.data);
      } catch {
        return null;
      }
    };

    es.addEventListener('snapshot', (ev) => {
      const s = parse(ev);
      if (!s) return;
      if (s.book) setBook(s.book);
      if (s.bbo) setBbo(s.bbo);
      if (s.ctx) setCtx(s.ctx);
      if (Array.isArray(s.trades)) setTrades(s.trades.slice(0, TRADE_RING));
      setStatus(s.status === 'live' ? 'live' : s.status || 'connecting');
      if (s.error) setError(s.error);
    });
    es.addEventListener('book', (ev) => {
      const b = parse(ev);
      if (!b) return;
      setBook(b);
      if (b.t) setLatencyMs(Math.max(0, Date.now() - b.t));
      setStatus('live');
    });
    es.addEventListener('bbo', (ev) => {
      const b = parse(ev);
      if (!b) return;
      setBbo(b);
      // Depth snapshots arrive every ~2-3 s; top-of-book arrives many times a second.
      // Patch the inside of the ladder so the best levels move at BBO speed.
      setBook((prev) => (prev ? mergeBboIntoBook(prev, b) : prev));
      if (b.t) setLatencyMs(Math.max(0, Date.now() - b.t));
      setStatus('live');
    });
    es.addEventListener('ctx', (ev) => {
      const c = parse(ev);
      if (c) setCtx(c);
    });
    es.addEventListener('trades', (ev) => {
      const t = parse(ev);
      if (!Array.isArray(t) || !t.length) return;
      setTrades((prev) => t.concat(prev).slice(0, TRADE_RING));
    });
    es.addEventListener('status', (ev) => {
      const s = parse(ev);
      if (!s) return;
      if (s.status) setStatus(s.status);
      setError(s.error || null);
    });
    es.onerror = () => {
      if (closed) return;
      // EventSource auto-reconnects; surface the state so the UI can show it.
      setStatus('reconnecting');
    };

    return () => {
      closed = true;
      es.close();
      if (esRef.current === es) esRef.current = null;
    };
  }, [coin, dex, apiPrefix, enabled, gen]);

  return { book, bbo, ctx, trades, status, error, latencyMs, reconnect };
}
